import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';

// utils
import axios from '../utils/axios';
import { isValidToken } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = Cookies.get('X-Access-Token');

        if (accessToken && isValidToken(accessToken)) {
          const user = JSON.parse(Cookies.get('UserDetails'));

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    await axios
      .post('/auth/login', {
        email,
        password,
      })
      .catch((err) => {
        throw new Error(err);
      });

    const user = JSON.parse(Cookies.get('UserDetails'));

    if (!user) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    } else {
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    }
  };

  const logout = async () => {
    await axios.post('/auth/logout');

    dispatch({
      type: 'LOGOUT'
    });
  };

  const forgotPassword = async (email) => {
    await axios.post('/auth/forgot-password', null, {
      params: {
        email
      },
    });
  };

  const resetPassword = async (email, password, token) => axios.post('/auth/reset-password', {
    email, password, token
  });

  const changePassword = async (currentPassword, newPassword) => axios.post('/auth/change-password', {
    currentPassword, newPassword
  });

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        resetPassword,
        forgotPassword,
        changePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
